import { forwardRef } from 'react';
import { Check } from '@phosphor-icons/react';
import { cn } from '../../utils/cn';

interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label text rendered next to the checkbox */
  label?: string;
  /** Error message shown below */
  error?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox({ label, error, className, id, disabled, ...props }, ref) {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="flex flex-col gap-1">
        <label
          htmlFor={inputId}
          className={cn(
            'inline-flex items-center gap-2 select-none min-h-[var(--touch-target-min)] lg:min-h-0',
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            className,
          )}
        >
          <span className="relative flex items-center justify-center">
            <input
              ref={ref}
              id={inputId}
              type="checkbox"
              disabled={disabled}
              className="peer sr-only"
              aria-invalid={error ? 'true' : undefined}
              aria-describedby={error ? `${inputId}-error` : undefined}
              {...props}
            />
            <span
              className={cn(
                'flex items-center justify-center h-5 w-5 rounded border transition-all duration-150',
                // WCAG 1.4.11 (>= 3:1 against the surface): unchecked boundary on border-control, checked fill on
                // accent-control (control-role tokens); border-focus is only the keyboard focus ring.
                'border-border-control bg-bg-secondary',
                'peer-checked:bg-accent-control peer-checked:border-accent-control',
                'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-border-focus',
                '[&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100',
                error && 'border-border-error',
              )}
            >
              <Check
                size={14}
                weight="bold"
                className="text-text-on-accent transition-opacity duration-150"
              />
            </span>
          </span>
          {label && (
            <span className="text-sm text-text-primary">{label}</span>
          )}
        </label>
        {error && (
          <p id={`${inputId}-error`} className="text-xs text-error-text ml-7" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);

export default Checkbox;
